import { useEffect, useRef } from "react";

import { useAppDispatch } from "app/hooks";
import { staffTasksApi } from "services/staff-tasks";
import { NotificationTypes } from "services/types/notifications";

// The top bar's "My Tasks" badge is read once on page load and otherwise only
// refreshed by this tab's own task writes, so a task assigned by a colleague, or
// removed by Suno support, left it stale until a reload (Intercom 215476409667635:
// 180 shown, 4 real). These two hooks keep it fresh without polling.

// Same wire string as `NewUserNotificationType` in hooks/use-notifications.tsx,
// restated so this module does not pull the whole socket hook in.
const NEW_USER_NOTIFICATION = "new.user.notification";

// Floor between refreshes when the person comes back to the tab. A product
// decision: every return refetching would put one count query per alt-tab on
// the backend, and the badge is a nudge, not a live feed.
export const TASK_COUNT_STALE_AFTER_MS = 10 * 60 * 1000;

type SocketMessage = {
  type?: string;
  message?: { type?: number };
} | null;

/**
 * Refresh the task counts when the backend pushes a task notification (assigned
 * to you, changed, completed, commented). Costs nothing until such an event.
 *
 * Call it exactly once, from the always-mounted bell (`NotificationsMenu`), with
 * that hook instance's `lastJsonMessage`: `useNotifications()` is used by several
 * components, and running this inside it would refetch once per instance.
 */
export function useRefreshTaskCountOnTaskNotification(
  lastJsonMessage: unknown
) {
  const dispatch = useAppDispatch();

  useEffect(() => {
    const payload = lastJsonMessage as SocketMessage;
    if (
      payload?.type === NEW_USER_NOTIFICATION &&
      payload.message?.type === NotificationTypes.TASK
    ) {
      dispatch(staffTasksApi.util.invalidateTags(["StaffTasksCount"]));
    }
  }, [lastJsonMessage, dispatch]);
}

type RefetchableQuery = {
  refetch: () => unknown;
  isUninitialized: boolean;
  // RTK Query's own request timestamps, straight off the hook result.
  startedTimeStamp?: number;
  fulfilledTimeStamp?: number;
};

/**
 * Refetch a query when the person returns to this tab, but only when its last
 * request is older than `staleAfterMs`. Covers changes that send no socket
 * message (a bulk cleanup, a colleague completing a task).
 *
 * The floor runs from when the last request STARTED, not when one last
 * succeeded: a failed request leaves `fulfilledTimeStamp` where it was, so a
 * floor on that alone would refetch on every return while the backend is
 * erroring. `focus` also fires each time someone clicks out of a TinyMCE
 * editor (an iframe), so that would be a request per click mid-incident.
 */
export function useRefetchOnReturnWhenStale(
  {
    refetch,
    isUninitialized,
    startedTimeStamp,
    fulfilledTimeStamp
  }: RefetchableQuery,
  staleAfterMs = TASK_COUNT_STALE_AFTER_MS
) {
  // Read through a ref so the listeners are bound once, not on every response.
  const latest = useRef({ refetch, isUninitialized, lastRequestAt: 0 });
  latest.current = {
    refetch,
    isUninitialized,
    lastRequestAt: Math.max(startedTimeStamp ?? 0, fulfilledTimeStamp ?? 0)
  };

  useEffect(() => {
    const onReturn = () => {
      if (document.visibilityState !== "visible") return;
      const { refetch, isUninitialized, lastRequestAt } = latest.current;
      // A skipped query has nothing to refetch (RTK throws on it), and one that
      // has never started is about to fetch on its own.
      if (isUninitialized || lastRequestAt === 0) return;
      if (Date.now() - lastRequestAt < staleAfterMs) return;
      refetch();
    };

    // `focus` and `visibilitychange` both fire on most returns; the second one
    // finds the request pending and RTK drops it (its condition refuses a
    // query that is already in flight).
    window.addEventListener("focus", onReturn);
    document.addEventListener("visibilitychange", onReturn);
    return () => {
      window.removeEventListener("focus", onReturn);
      document.removeEventListener("visibilitychange", onReturn);
    };
  }, [staleAfterMs]);
}
